import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "@mui/material";
import intl from "react-intl-universal";
import { Link } from "react-router-dom";
import {
  TbArrowLeft,
  TbCheck,
  TbKey,
  TbLock,
  TbLockOff,
  TbMailForward,
  TbPencil,
  TbSearch,
  TbTrash,
  TbUserOff,
  TbUserPlus,
} from "react-icons/tb";
import { ModalCloseButton } from "../../../../../Components/Molecules/ValutDialog";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useToast } from "../../../../../Hooks/toast";
import {
  BAAS_ENTITY_FEATURE_OPTIONS,
  fetchBaasEntityFeatures,
  setBaasEntityFeatures,
} from "../../../../../Services/baasEntityFeaturesApi";
import { fetchBaasEntities } from "../../../../../Services/baasEntidadesApi";
import { formatBaasConsoleConflictMessage } from "../../../../../Services/baasConsoleClient";
import {
  assignableBaasUserRoles,
  BAAS_GROUP_PERMISSIONS,
  BAAS_GROUP_ROLE_PERMISSIONS,
  createBaasUser,
  lookupBaasPersonByDocument,
  removeBaasUser,
  resendBaasUserAccessEmail,
  setBaasUserLoginPassword,
  setBaasUserTransactionalPassword,
  resetBaasUserTransactionalPassword,
  syncBaasUserInstanceAccess,
  updateBaasUser,
} from "../../../../../Services/baasUsersApi";
import { useBaasCapabilities } from "../../../../../Hooks/useBaasCapabilities";
import { useBaasPlatformCreateAccess } from "../../../../../Hooks/useBaasPlatformCreateAccess";
import { BaasSearchableSelect } from "./BaasSearchableSelect";
import { PhonePartsFields } from "../../Entidades/components/baasEntidadeCreateFields";
import {
  buildPhoneDialOptions,
  composeE164FromParts,
  createEmptyPhoneParts,
  isPhonePartsComplete,
  mapPhonePartsFromPayload,
} from "../../../../../Services/baasPhoneParts";

const TAB_DADOS = "dados";
const TAB_INSTANCES = "instances";
const TAB_ACCESS = "access";

/** Create-flow wizard steps (edit keeps tabs). */
const CREATE_STEP_DADOS = 0;
const CREATE_STEP_ACCESS = 1;

const DADOS_DOC = "document";
const DADOS_FORM = "form";

const CreateUserStepper = ({ currentStep = 0, steps = [] }) => (
  <nav
    aria-label={intl.get("baas.users.steps.label")}
    className="mb-5 w-full"
  >
    <ol className="flex w-full items-center">
      {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isActive = index === currentStep;
        const isLast = index === steps.length - 1;

        return (
          <li key={step.id} className={`flex items-center ${isLast ? "" : "flex-1"}`}>
            <div className="flex min-w-[2.25rem] flex-col items-center gap-1 sm:min-w-[3.5rem]">
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold transition ${
                  isCompleted
                    ? "bg-[#e7a73f] text-white"
                    : isActive
                      ? "border-2 border-[#e7a73f] bg-white text-[#a66f1d]"
                      : "border border-gray-200 bg-white text-gray-400 dark:border-brand-dark-border"
                }`}
              >
                {isCompleted ? <TbCheck className="h-3.5 w-3.5" aria-hidden /> : index + 1}
              </div>
              <span
                className={`hidden text-center text-[10px] font-semibold sm:block ${
                  isActive || isCompleted
                    ? "text-brand-dark dark:text-gray-100"
                    : "text-gray-400"
                }`}
              >
                {step.label}
              </span>
            </div>
            {!isLast ? (
              <div
                className={`mx-1 h-0.5 min-w-[0.5rem] flex-1 rounded-full sm:mx-1.5 ${
                  isCompleted ? "bg-[#e7a73f]" : "bg-gray-200 dark:bg-brand-dark-border"
                }`}
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  </nav>
);

const MODAL_CLASS =
  "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !w-[calc(100%-2rem)] !max-w-4xl outline-none";

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] focus:ring-2 focus:ring-[#e7a73f]/20 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const disabledInputClass =
  "h-10 w-full cursor-not-allowed rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm text-gray-400 outline-none dark:border-brand-dark-border dark:bg-brand-dark-elevated/50 dark:text-gray-500";

const checkboxClass =
  "h-4 w-4 rounded border-gray-300 accent-[#e7a73f] text-[#e7a73f] focus:outline-none focus:ring-2 focus:ring-[#e7a73f]/30";

const digitsOnly = (value) => String(value ?? "").replace(/\D/g, "");

const formatDocument = (value) => {
  const digits = digitsOnly(value).slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};

const roleLabelKey = {
  super_user: "baas.users.role.super_user",
  admin: "baas.users.role.admin",
  operator: "baas.users.role.operator",
  viewer: "baas.users.role.viewer",
};

const featureLabelKey = (code) => `baas.instances.createPage.feature.${code}`;
const featureHintKey = (code) => `baas.instances.createPage.featureHint.${code}`;

const sourceLabelKey = {
  instance: "baas.users.entityPermissions.source.instance",
  entity: "baas.users.entityPermissions.source.entity",
  denied_by_instance: "baas.users.entityPermissions.source.deniedByInstance",
};

const statusStyles = {
  active: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  invited: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  suspended: "bg-gray-100 text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400",
};

const accessStatusStyles = {
  completed: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  pending: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  no_email: "bg-gray-100 text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400",
};

/** Memberships live on Prod anchors — prefer production cards in the picker. */
const instancesForMembershipPicker = (instances = []) => {
  const prod = instances.filter(
    (item) => String(item.environment || "").toLowerCase() === "production",
  );
  return prod.length > 0 ? prod : instances;
};

const isProductionEnv = (item) =>
  item?.isProduction === true ||
  ["production", "prod"].includes(String(item?.environment || "").toLowerCase());

/** Resolve console card for a membership; prefer Prod when SQL ids collide with HML twins. */
const resolveInstanceForMembershipRow = (membership, instances = [], fallback = null) => {
  if (!membership) return fallback;

  const baasId = Number(membership.baasInstanceId);
  if (Number.isFinite(baasId) && baasId > 0) {
    const matches = instances.filter((item) => Number(item.baasInstanceId) === baasId);
    const prod = matches.find((item) => isProductionEnv(item));
    if (prod) return prod;
    if (matches[0]) return matches[0];
  }

  const bySelectionId = instances.find(
    (item) => String(item.id) === String(membership.instanceId),
  );
  if (bySelectionId) return bySelectionId;

  return fallback;
};

const productsToEnabledMap = (products = []) =>
  Object.fromEntries(
    BAAS_ENTITY_FEATURE_OPTIONS.map((option) => {
      const product = products.find((item) => item.code === option.code);
      return [option.code, product ? Boolean(product.enabled) : option.defaultEnabled !== false];
    }),
  );

const productsToSourceMap = (products = []) =>
  Object.fromEntries(
    BAAS_ENTITY_FEATURE_OPTIONS.map((option) => {
      const product = products.find((item) => item.code === option.code);
      return [option.code, product?.source ?? "instance"];
    }),
  );

const sourceToneClass = {
  instance: "text-sky-700 dark:text-sky-300",
  entity: "text-amber-700 dark:text-amber-300",
  denied_by_instance: "text-gray-400 dark:text-gray-500",
};

const FeatureToggleRow = ({
  label,
  hint,
  source,
  checked,
  onChange,
  disabled,
  footer = null,
}) => (
  <div
    className={`rounded-lg border px-3 py-2.5 dark:border-brand-dark-border ${
      disabled
        ? "border-gray-200 bg-gray-50/80 dark:bg-brand-dark-elevated/30"
        : "border-gray-200 dark:border-brand-dark-border"
    }`}
  >
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-brand-dark dark:text-gray-100">{label}</p>
        {hint ? <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p> : null}
        {source ? (
          <p
            className={`mt-0.5 text-[11px] uppercase tracking-wide ${
              sourceToneClass[source] ?? sourceToneClass.instance
            }`}
          >
            {intl.get(sourceLabelKey[source] ?? sourceLabelKey.instance)}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled}
        className={`relative h-6 w-11 shrink-0 rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e7a73f]/40 ${
          checked ? "bg-[#e7a73f]" : "bg-gray-300 dark:bg-brand-dark-border"
        } ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
        onClick={() => {
          if (!disabled) onChange(!checked);
        }}
        disabled={disabled}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${
            checked ? "left-5" : "left-0.5"
          }`}
        />
      </button>
    </div>
    {footer}
  </div>
);

/**
 * @param {{
 *   open: boolean,
 *   person?: object|null,
 *   user?: object|null,
 *   scope?: "org"|"instance",
 *   fixedInstance?: object|null,
 *   onClose: () => void,
 *   onSaved?: () => void,
 * }} props
 */
export const BaasUserCreateModal = ({
  open,
  person = null,
  user = null,
  scope = "org",
  fixedInstance = null,
  onClose,
  onSaved,
}) => {
  const { addToast } = useToast();
  const { instances } = useBaasInstance();
  const { isMaster: capsMaster } = useBaasCapabilities();
  const { isMaster: platformMaster, isValutConfigAdmin } = useBaasPlatformCreateAccess();
  /** Platform master (`super_user`) — assign super_user + manage instance links. */
  const isMaster = Boolean(capsMaster || platformMaster);
  // Never use Carteira AuthContext hasPermission("admin") — often defaulted for wallet admins.
  const isValutAdmin = Boolean(isValutConfigAdmin || isMaster);
  const isInstanceScope = scope === "instance";
  const canManageInstanceLinks = isMaster;
  const pickerInstances = useMemo(
    () => instancesForMembershipPicker(instances),
    [instances],
  );
  const editingPerson = person ?? (user
    ? {
        personId: user.personId,
        entityId: user.entityId ?? null,
        name: user.name,
        email: user.email,
        memberships: [user],
      }
    : null);
  const isEdit = Boolean(editingPerson);

  const [activeTab, setActiveTab] = useState(TAB_DADOS);
  /** Create-only wizard index: 0 = Dados, 1 = Acesso (edit keeps tabs). */
  const [createStep, setCreateStep] = useState(CREATE_STEP_DADOS);
  const [dadosPhase, setDadosPhase] = useState(DADOS_DOC);
  const [nationalRegistration, setNationalRegistration] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState(createEmptyPhoneParts);
  const [lookingUp, setLookingUp] = useState(false);
  const [personFound, setPersonFound] = useState(false);
  const [hasPlatformAccess, setHasPlatformAccess] = useState(false);
  const [alreadyLinked, setAlreadyLinked] = useState(null);
  const [fieldsLocked, setFieldsLocked] = useState(false);
  const [selectedInstanceIds, setSelectedInstanceIds] = useState([]);
  const [instanceFilter, setInstanceFilter] = useState("");
  const [role, setRole] = useState("operator");
  const [status, setStatus] = useState("active");
  const [permissions, setPermissions] = useState(BAAS_GROUP_ROLE_PERMISSIONS.operator);
  const [canViewProduction, setCanViewProduction] = useState(true);
  const [canViewHomolog, setCanViewHomolog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [membershipActionId, setMembershipActionId] = useState(null);
  /** null = not hydrated for this open; [] = explicitly empty after Remover. */
  const [liveMemberships, setLiveMemberships] = useState(null);
  const [resendingAccess, setResendingAccess] = useState(false);
  const [loginPassword, setLoginPassword] = useState("");
  const [loginPasswordConfirm, setLoginPasswordConfirm] = useState("");
  const [savingLoginPassword, setSavingLoginPassword] = useState(false);
  const [txPassword, setTxPassword] = useState("");
  const [txPasswordConfirm, setTxPasswordConfirm] = useState("");
  const [savingTxPassword, setSavingTxPassword] = useState(false);
  const [resettingTxPassword, setResettingTxPassword] = useState(false);
  const [formError, setFormError] = useState(null);

  const roleOptions = useMemo(() => {
    const base = assignableBaasUserRoles(isMaster);
    // Keep current super_user visible when a non-master opens an existing master row
    // (select locked) so we never silently demote on save.
    if (!isMaster && role === "super_user" && !base.includes("super_user")) {
      return ["super_user", ...base];
    }
    return base;
  }, [isMaster, role]);
  const roleSelectLocked = !isMaster && role === "super_user";
  const phoneDialOptions = useMemo(() => buildPhoneDialOptions(), []);

  const [entityId, setEntityId] = useState("");
  const [entityOptions, setEntityOptions] = useState([]);
  const [entityOptionsLoading, setEntityOptionsLoading] = useState(false);
  const [entityFeaturesLoading, setEntityFeaturesLoading] = useState(false);
  const [entityFeatureEnabled, setEntityFeatureEnabled] = useState({});
  const [entityFeatureSource, setEntityFeatureSource] = useState({});
  const [entityFeaturesDirty, setEntityFeaturesDirty] = useState(false);
  const [entityFeaturesError, setEntityFeaturesError] = useState(null);
  /** Recovered pers_… when list DTO omits entity_id (legacy / null BaasExternalId). */
  const [resolvedEntityIdFallback, setResolvedEntityIdFallback] = useState(null);
  const entityFeaturesDirtyRef = useRef(false);
  const entityContextInstanceRef = useRef(null);

  const existingMemberships = liveMemberships != null
    ? liveMemberships
    : (editingPerson?.memberships ?? []);
  /** Linked pers_… from person card or any membership row (never hide when present). */
  const personEntityId = useMemo(() => {
    const candidates = [
      editingPerson?.entityId,
      ...(liveMemberships ?? []).map((m) => m?.entityId),
      ...(editingPerson?.memberships ?? []).map((m) => m?.entityId),
    ];
    for (const candidate of candidates) {
      if (candidate != null && String(candidate).trim() !== "") {
        return String(candidate).trim();
      }
    }
    return null;
  }, [editingPerson, liveMemberships]);

  const lockedInstanceIds = useMemo(() => {
    const ids = new Set();
    for (const membership of existingMemberships) {
      if (!membership?.id) continue;
      if (membership.instanceId != null && membership.instanceId !== "") {
        ids.add(String(membership.instanceId));
      }
      const baasId = Number(membership.baasInstanceId);
      if (!Number.isFinite(baasId) || baasId <= 0) continue;
      // Lock Prod + HML sibling cards for the same SQL instance id.
      for (const instance of instances) {
        if (Number(instance.baasInstanceId) === baasId) {
          ids.add(String(instance.id));
        }
      }
    }
    return ids;
  }, [existingMemberships, instances]);

  const entityContextInstance = useMemo(() => {
    if (isInstanceScope) return fixedInstance;
    return null;
  }, [isInstanceScope, fixedInstance]);

  const entityContextInstanceId = entityContextInstance?.id
    ? String(entityContextInstance.id)
    : "";

  const selectedInstances = useMemo(() => {
    if (isInstanceScope && fixedInstance) {
      return [fixedInstance];
    }
    return pickerInstances.filter((instance) =>
      selectedInstanceIds.includes(String(instance.id)),
    );
  }, [isInstanceScope, fixedInstance, pickerInstances, selectedInstanceIds]);

  const addablePickerInstances = useMemo(
    () => pickerInstances.filter((item) => !lockedInstanceIds.has(String(item.id))),
    [pickerInstances, lockedInstanceIds],
  );

  const filteredAddableInstances = useMemo(() => {
    const term = instanceFilter.trim().toLowerCase();
    if (!term) return addablePickerInstances;
    return addablePickerInstances.filter((instance) => {
      const haystack = [instance.name, instance.id, instance.environment]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [addablePickerInstances, instanceFilter]);

  useEffect(() => {
    entityFeaturesDirtyRef.current = entityFeaturesDirty;
  }, [entityFeaturesDirty]);

  useEffect(() => {
    entityContextInstanceRef.current = entityContextInstance;
  }, [entityContextInstance]);

  /** Locked entity for product gates — always the person profile entity (pers_…). */
  const lockedEntityId = personEntityId
    ? String(personEntityId)
    : resolvedEntityIdFallback
      ? String(resolvedEntityIdFallback)
      : "";

  /** Options for the locked select — keep linked entity visible even before fetch. */
  const lockedEntitySelectOptions = useMemo(() => {
    if (!lockedEntityId) return [];
    const fromList = entityOptions.find((item) => item.id === lockedEntityId);
    const seed = {
      id: lockedEntityId,
      name: fromList?.name || editingPerson?.name || lockedEntityId,
      type: fromList?.type || "pf",
      label: fromList?.name
        ? `${fromList.name} (${intl.get("baas.users.entityPermissions.personBadge")})`
        : editingPerson?.name
          ? `${editingPerson.name} (${intl.get("baas.users.entityPermissions.personBadge")})`
          : lockedEntityId,
    };
    if (entityOptions.length === 0) return [seed];
    if (!fromList) return [seed, ...entityOptions];
    return entityOptions.map((entity) =>
      entity.id === lockedEntityId
        ? {
            ...entity,
            label: `${entity.name} (${intl.get("baas.users.entityPermissions.personBadge")})`,
          }
        : entity,
    );
  }, [lockedEntityId, entityOptions, editingPerson?.name]);

  useEffect(() => {
    if (!open) {
      setResolvedEntityIdFallback(null);
      return;
    }

    setActiveTab(TAB_DADOS);
    setCreateStep(CREATE_STEP_DADOS);
    setInstanceFilter("");
    setEntityFeaturesDirty(false);
    entityFeaturesDirtyRef.current = false;
    setEntityFeaturesError(null);
    setEntityFeatureEnabled({});
    setEntityFeatureSource({});
    setMembershipActionId(null);
    setResolvedEntityIdFallback(null);
    setLookingUp(false);

    const linkedId =
      editingPerson?.entityId != null && String(editingPerson.entityId).trim() !== ""
        ? String(editingPerson.entityId).trim()
        : (editingPerson?.memberships ?? []).find(
            (m) => m?.entityId != null && String(m.entityId).trim() !== "",
          )?.entityId;

    // Seed locked option immediately so Entidade/perfil never disappears while loading.
    if (linkedId) {
      const id = String(linkedId).trim();
      setEntityOptions([
        {
          id,
          name: editingPerson?.name || id,
          type: "pf",
        },
      ]);
      setEntityId(id);
    } else {
      setEntityOptions([]);
      setEntityId("");
    }

    if (editingPerson) {
      const fallback = isInstanceScope && fixedInstance ? fixedInstance : null;
      const memberships = (editingPerson.memberships ?? []).map((membership) => {
        const resolved = resolveInstanceForMembershipRow(membership, instances, fallback);
        if (!resolved) return membership;
        return {
          ...membership,
          instanceId: resolved.id,
          baasInstanceId: resolved.baasInstanceId ?? membership.baasInstanceId,
        };
      });
      setLiveMemberships(memberships);
      setDadosPhase(DADOS_FORM);
      setPersonFound(true);
      setFieldsLocked(true);
      setName(editingPerson.name ?? "");
      setEmail(editingPerson.email ?? "");
      setPhone(
        mapPhonePartsFromPayload({
          phone_country_dial_code: editingPerson.phoneCountryDialCode,
          phone_area_code: editingPerson.phoneAreaCode,
          phone_local_number: editingPerson.phoneLocalNumber,
          phone_number: editingPerson.phoneNumber ?? editingPerson.phone,
        }),
      );
      setNationalRegistration(
        formatDocument(editingPerson.nationalRegistration ?? editingPerson.document ?? ""),
      );
      const activeIds = memberships.map((m) => String(m.instanceId));
      setSelectedInstanceIds(activeIds);
      const scopedMembership = isInstanceScope && fixedInstance
        ? memberships.find((m) => String(m.instanceId) === String(fixedInstance.id))
        : null;
      const firstActive =
        scopedMembership ??
        memberships.find((m) => m.status === "active") ??
        memberships[0];
      setRole(firstActive?.role ?? "operator");
      setStatus(firstActive?.status === "suspended" ? "suspended" : "active");
      setPermissions(
        Array.isArray(firstActive?.permissions) && firstActive.permissions.length > 0
          ? firstActive.permissions
          : BAAS_GROUP_ROLE_PERMISSIONS[firstActive?.role] ?? BAAS_GROUP_ROLE_PERMISSIONS.operator,
      );
      setCanViewProduction(firstActive?.canViewProduction !== false);
      setCanViewHomolog(firstActive?.canViewHomolog !== false);
      return;
    }

    setLiveMemberships(null);
    setDadosPhase(DADOS_DOC);
    setPersonFound(false);
    setFieldsLocked(false);
    setNationalRegistration("");
    setName("");
    setEmail("");
    setPhone(createEmptyPhoneParts());
    if (isInstanceScope && fixedInstance) {
      setSelectedInstanceIds([String(fixedInstance.id)]);
    } else {
      const defaultId = pickerInstances[0]?.id ? String(pickerInstances[0].id) : "";
      setSelectedInstanceIds(defaultId ? [defaultId] : []);
    }
    setRole("operator");
    setStatus("active");
    setPermissions(BAAS_GROUP_ROLE_PERMISSIONS.operator);
    setCanViewProduction(true);
    setCanViewHomolog(false);
  }, [open, editingPerson, pickerInstances, instances, isInstanceScope, fixedInstance]);

  // Keep entityId locked to the person profile entity whenever present.
  useEffect(() => {
    if (!open || !isInstanceScope) return;
    setEntityId(lockedEntityId);
  }, [open, isInstanceScope, lockedEntityId]);

  // Enrich locked entity display name from the entities API (optional).
  // Also recover missing entity_id by matching the person name among pers_… entities.
  const onAccessPanel =
    isEdit
      ? activeTab === TAB_ACCESS
      : createStep === CREATE_STEP_ACCESS;

  useEffect(() => {
    if (!open || !isInstanceScope || !onAccessPanel || !entityContextInstanceId) {
      return undefined;
    }

    const instance = entityContextInstanceRef.current;
    if (!instance) return undefined;

    let cancelled = false;
    setEntityOptionsLoading(true);

    (async () => {
      try {
        const entities = await fetchBaasEntities(instance, { pageSize: 100 });
        if (cancelled) return;
        const options = entities.map((entity) => ({
          id: String(entity.id),
          name: entity.name || entity.id,
          type: entity.type,
        }));

        let effectiveLockedId = lockedEntityId;
        if (!effectiveLockedId && editingPerson?.name) {
          const personName = String(editingPerson.name).trim().toLowerCase();
          const personMatches = options.filter((entity) => {
            const id = String(entity.id || "");
            if (!id.toLowerCase().startsWith("pers_")) return false;
            const type = String(entity.type || "").toLowerCase();
            if (type && type !== "pf" && type !== "person") return false;
            return String(entity.name || "").trim().toLowerCase() === personName;
          });
          if (personMatches.length === 1) {
            effectiveLockedId = personMatches[0].id;
            setResolvedEntityIdFallback(effectiveLockedId);
          }
        }

        if (effectiveLockedId && !options.some((item) => item.id === effectiveLockedId)) {
          options.unshift({
            id: effectiveLockedId,
            name: editingPerson?.name || effectiveLockedId,
            type: "pf",
          });
        }
        setEntityOptions(options);
      } catch {
        // Keep the seeded locked option — do not clear the field on fetch failure.
        if (!cancelled && lockedEntityId) {
          setEntityOptions((current) =>
            current.some((item) => item.id === lockedEntityId)
              ? current
              : [
                  {
                    id: lockedEntityId,
                    name: editingPerson?.name || lockedEntityId,
                    type: "pf",
                  },
                ],
          );
        }
      } finally {
        if (!cancelled) setEntityOptionsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    isInstanceScope,
    onAccessPanel,
    entityContextInstanceId,
    lockedEntityId,
    editingPerson?.name,
  ]);

  // Load effective features when entity + instance are ready.
  useEffect(() => {
    if (!open || !isInstanceScope || !onAccessPanel || !entityContextInstanceId || !entityId) {
      return undefined;
    }

    const instance = entityContextInstanceRef.current;
    if (!instance) return undefined;

    let cancelled = false;
    setEntityFeaturesLoading(true);
    setEntityFeaturesError(null);

    (async () => {
      try {
        const response = await fetchBaasEntityFeatures(instance, entityId);
        if (cancelled) return;
        if (entityFeaturesDirtyRef.current) return;
        setEntityFeatureEnabled(productsToEnabledMap(response.products));
        setEntityFeatureSource(productsToSourceMap(response.products));
        setEntityFeaturesDirty(false);
      } catch (error) {
        if (!cancelled && !entityFeaturesDirtyRef.current) {
          setEntityFeatureEnabled({});
          setEntityFeatureSource({});
          setEntityFeaturesError(
            error?.detail || error?.message || intl.get("baas.users.entityPermissions.loadError"),
          );
        }
      } finally {
        if (!cancelled) setEntityFeaturesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, isInstanceScope, onAccessPanel, entityContextInstanceId, entityId]);

  const resetForm = () => {
    setActiveTab(TAB_DADOS);
    setCreateStep(CREATE_STEP_DADOS);
    setDadosPhase(DADOS_DOC);
    setNationalRegistration("");
    setName("");
    setEmail("");
    setPhone(createEmptyPhoneParts());
    setLookingUp(false);
    setPersonFound(false);
    setHasPlatformAccess(false);
    setAlreadyLinked(null);
    setFieldsLocked(false);
    setSelectedInstanceIds([]);
    setInstanceFilter("");
    setRole("operator");
    setStatus("active");
    setPermissions(BAAS_GROUP_ROLE_PERMISSIONS.operator);
    setSubmitting(false);
    setMembershipActionId(null);
    setLiveMemberships(null);
    setEntityId("");
    setEntityOptions([]);
    setEntityFeatureEnabled({});
    setEntityFeatureSource({});
    setEntityFeaturesDirty(false);
    entityFeaturesDirtyRef.current = false;
    setEntityFeaturesError(null);
    setResendingAccess(false);
    setLoginPassword("");
    setLoginPasswordConfirm("");
    setSavingLoginPassword(false);
    setTxPassword("");
    setTxPasswordConfirm("");
    setSavingTxPassword(false);
    setFormError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const resolveInstanceForMembership = (membership) =>
    resolveInstanceForMembershipRow(
      membership,
      instances,
      isInstanceScope && fixedInstance ? fixedInstance : null,
    );

  const toggleAddableInstance = (instanceId) => {
    const id = String(instanceId);
    if (lockedInstanceIds.has(id)) return;
    setSelectedInstanceIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  };

  const handleRoleChange = (nextRole) => {
    if (roleSelectLocked) return;
    if (!isMaster && nextRole === "super_user") return;
    setRole(nextRole);
    setPermissions(BAAS_GROUP_ROLE_PERMISSIONS[nextRole] ?? []);
  };

  const togglePermission = (permission) => {
    setPermissions((current) =>
      current.includes(permission)
        ? current.filter((item) => item !== permission)
        : [...current, permission],
    );
  };

  const toggleEntityFeature = (code, enabled) => {
    setEntityFeatureEnabled((current) => ({ ...current, [code]: enabled }));
    setEntityFeatureSource((current) => {
      const previous = current[code];
      if (previous === "denied_by_instance" && !isValutAdmin) {
        return current;
      }
      return { ...current, [code]: "entity" };
    });
    setEntityFeaturesDirty(true);
    entityFeaturesDirtyRef.current = true;
  };

  const handleResendAccessEmail = async () => {
    const membership =
      existingMemberships.find((m) =>
        fixedInstance
          ? String(m.instanceId) === String(fixedInstance.id)
          : m.status === "active",
      ) ||
      existingMemberships[0];
    const membershipId = alreadyLinked?.userId || membership?.id;
    const instance = membership
      ? resolveInstanceForMembership(membership)
      : fixedInstance;
    if (!instance || !membershipId) {
      setFormError(
        [
          intl.get("baas.users.access.resendFailed"),
          intl.get("baas.users.access.instanceRequired"),
        ]
          .filter(Boolean)
          .join(" — "),
      );
      return;
    }

    const accessStatus = editingPerson?.accessStatus || "pending";
    if (accessStatus === "no_email" || !String(email || "").includes("@")) {
      setFormError(
        [
          intl.get("baas.users.access.resendFailed"),
          intl.get("baas.users.access.noEmail"),
        ]
          .filter(Boolean)
          .join(" — "),
      );
      return;
    }

    const confirmed = window.confirm(
      intl.get("baas.users.access.resendConfirm", { email: email || "" }),
    );
    if (!confirmed) return;

    setResendingAccess(true);
    setFormError(null);
    try {
      const result = await resendBaasUserAccessEmail(instance, membershipId);
      const masked = String(result?.email || "").trim();
      addToast({
        type: "success",
        title: masked
          ? intl.get("baas.users.access.resendSuccessTo", { email: masked })
          : intl.get("baas.users.access.resendSuccess"),
      });
    } catch (error) {
      setFormError(
        [
          intl.get("baas.users.access.resendFailed"),
          formatBaasConsoleConflictMessage(
            error,
            error?.detail || error?.message || null,
          ),
        ]
          .filter(Boolean)
          .join(" — "),
      );
    } finally {
      setResendingAccess(false);
    }
  };

  const resolveAccessMembership = () => {
    const membership =
      existingMemberships.find((m) =>
        fixedInstance
          ? String(m.instanceId) === String(fixedInstance.id)
          : m.status === "active",
      ) ||
      existingMemberships[0];
    const instance = membership
      ? resolveInstanceForMembership(membership)
      : fixedInstance;
    return { membership, instance };
  };

  const handleSetLoginPassword = async () => {
    const { membership, instance } = resolveAccessMembership();
    if (!instance || !membership?.id) {
      setFormError(
        [
          intl.get("baas.users.access.passwordFailed"),
          intl.get("baas.users.access.instanceRequired"),
        ]
          .filter(Boolean)
          .join(" — "),
      );
      return;
    }

    if (!loginPassword || loginPassword.length < 8) {
      setFormError(intl.get("baas.users.access.passwordMinLength"));
      return;
    }
    if (loginPassword !== loginPasswordConfirm) {
      setFormError(intl.get("baas.users.access.passwordMismatch"));
      return;
    }

    const confirmed = window.confirm(intl.get("baas.users.access.passwordConfirm"));
    if (!confirmed) return;

    setSavingLoginPassword(true);
    setFormError(null);
    try {
      await setBaasUserLoginPassword(instance, membership.id, {
        password: loginPassword,
        confirmPassword: loginPasswordConfirm,
      });
      setLoginPassword("");
      setLoginPasswordConfirm("");
      addToast({
        type: "success",
        title: intl.get("baas.users.access.passwordSuccess"),
      });
    } catch (error) {
      setFormError(
        [
          intl.get("baas.users.access.passwordFailed"),
          formatBaasConsoleConflictMessage(
            error,
            error?.detail || error?.message || null,
          ),
        ]
          .filter(Boolean)
          .join(" — "),
      );
    } finally {
      setSavingLoginPassword(false);
    }
  };

  const handleSetTransactionalPassword = async () => {
    const { membership, instance } = resolveAccessMembership();
    if (!instance || !membership?.id) {
      setFormError(
        [
          intl.get("baas.users.access.txPasswordFailed"),
          intl.get("baas.users.access.instanceRequired"),
        ]
          .filter(Boolean)
          .join(" — "),
      );
      return;
    }

    if (!/^\d{4}$/.test(txPassword)) {
      setFormError(intl.get("baas.users.access.txPasswordDigits"));
      return;
    }
    if (txPassword !== txPasswordConfirm) {
      setFormError(intl.get("baas.users.access.passwordMismatch"));
      return;
    }

    const confirmed = window.confirm(intl.get("baas.users.access.txPasswordConfirm"));
    if (!confirmed) return;

    setSavingTxPassword(true);
    setFormError(null);
    try {
      await setBaasUserTransactionalPassword(instance, membership.id, {
        password: txPassword,
        confirmPassword: txPasswordConfirm,
      });
      setTxPassword("");
      setTxPasswordConfirm("");
      addToast({
        type: "success",
        title: intl.get("baas.users.access.txPasswordSuccess"),
      });
    } catch (error) {
      setFormError(
        [
          intl.get("baas.users.access.txPasswordFailed"),
          formatBaasConsoleConflictMessage(
            error,
            error?.detail || error?.message || null,
          ),
        ]
          .filter(Boolean)
          .join(" — "),
      );
    } finally {
      setSavingTxPassword(false);
    }
  };

  const handleResetTransactionalPassword = async () => {
    if (!isInstanceScope) return;
    const { membership, instance } = resolveAccessMembership();
    if (!instance || !membership?.id) {
      setFormError(
        [
          intl.get("baas.users.access.txPasswordResetFailed"),
          intl.get("baas.users.access.instanceRequired"),
        ]
          .filter(Boolean)
          .join(" — "),
      );
      return;
    }

    const confirmed = window.confirm(intl.get("baas.users.access.txPasswordResetConfirm"));
    if (!confirmed) return;

    setResettingTxPassword(true);
    setFormError(null);
    try {
      await resetBaasUserTransactionalPassword(instance, membership.id);
      addToast({
        type: "success",
        title: intl.get("baas.users.access.txPasswordResetSuccess"),
      });
    } catch (error) {
      setFormError(
        [
          intl.get("baas.users.access.txPasswordResetFailed"),
          formatBaasConsoleConflictMessage(
            error,
            error?.detail || error?.message || null,
          ),
        ]
          .filter(Boolean)
          .join(" — "),
      );
    } finally {
      setResettingTxPassword(false);
    }
  };

  const handleMembershipStatus = async (membership, nextStatus) => {
    const instance = resolveInstanceForMembership(membership);
    if (!instance || !membership?.id) {
      setFormError(
        [
          intl.get("baas.users.saveError"),
          intl.get("baas.users.membership.instanceResolveFailed"),
        ]
          .filter(Boolean)
          .join(" — "),
      );
      return;
    }

    setMembershipActionId(membership.id);
    setFormError(null);
    try {
      const updated = await updateBaasUser(instance, membership.id, {
        role: membership.role,
        permissions: membership.permissions,
        status: nextStatus,
        groupId: membership.groupId ?? null,
      });
      setLiveMemberships((current) => {
        const base = current != null ? current : existingMemberships;
        return base.map((item) => (item.id === membership.id ? { ...item, ...updated } : item));
      });
      if (isInstanceScope) {
        setStatus(nextStatus === "suspended" ? "suspended" : "active");
      }
      addToast({
        type: "success",
        title: intl.get(
          nextStatus === "suspended"
            ? "baas.users.membership.deactivateSuccess"
            : "baas.users.membership.activateSuccess",
        ),
      });
      onSaved?.();
    } catch (error) {
      setFormError(error?.detail || error?.message || intl.get("baas.users.saveError"));
    } finally {
      setMembershipActionId(null);
    }
  };

  const handleRemoveMembership = async (membership) => {
    // Org Instâncias tab: only master may unlink. Instance page Remover stays for admins.
    if (!isInstanceScope && !canManageInstanceLinks) {
      setFormError(intl.get("baas.users.membership.instanceLinksLocked"));
      return;
    }

    const instance = resolveInstanceForMembership(membership);
    if (!instance || !membership?.id) {
      setFormError(
        [
          intl.get("baas.users.saveError"),
          intl.get("baas.users.membership.instanceResolveFailed"),
        ]
          .filter(Boolean)
          .join(" — "),
      );
      return;
    }

    const instanceName = instance.name || membership.instanceId;
    const confirmed = window.confirm(
      intl.get("baas.users.membership.removeConfirm", { instance: instanceName }),
    );
    if (!confirmed) return;

    setMembershipActionId(membership.id);
    setFormError(null);
    try {
      await removeBaasUser(instance, membership.id);
      const base = liveMemberships != null ? liveMemberships : existingMemberships;
      const remaining = base.filter((item) => item.id !== membership.id);
      setLiveMemberships(remaining);
      setSelectedInstanceIds((current) =>
        current.filter((id) => {
          if (id === String(membership.instanceId)) return false;
          const baasId = Number(membership.baasInstanceId);
          if (!Number.isFinite(baasId) || baasId <= 0) return true;
          const match = instances.find((item) => String(item.id) === String(id));
          return !(match && Number(match.baasInstanceId) === baasId);
        }),
      );
      addToast({
        type: "success",
        title: intl.get("baas.users.membership.removeSuccess"),
      });
      onSaved?.();
      if (isInstanceScope || remaining.length === 0) {
        handleClose();
      }
    } catch (error) {
      setFormError(error?.detail || error?.message || intl.get("baas.users.saveError"));
    } finally {
      setMembershipActionId(null);
    }
  };

  const handleLookupDocument = async () => {
    if (lookingUp || submitting) return;
    const docDigits = digitsOnly(nationalRegistration);
    if (docDigits.length !== 11 && docDigits.length !== 14) {
      setFormError(intl.get("baas.users.errors.documentRequired"));
      return;
    }

    setLookingUp(true);
    setFormError(null);
    try {
      const result = await lookupBaasPersonByDocument(nationalRegistration, {
        instanceId: isInstanceScope
          ? fixedInstance?.baasInstanceId ?? fixedInstance?.id
          : null,
      });

      if (result.alreadyMember) {
        const person = result.person;
        setPersonFound(true);
        setHasPlatformAccess(Boolean(result.hasPlatformAccess));
        setAlreadyLinked({
          userId: result.userId,
          personId: person?.personId ?? null,
        });
        setFieldsLocked(true);
        setNationalRegistration(formatDocument(person?.nationalRegistration || docDigits));
        setName(
          [person?.name, person?.middleName, person?.lastName].filter(Boolean).join(" ").trim()
            || person?.name
            || "",
        );
        setEmail(person?.email ?? "");
        setPhone(
          mapPhonePartsFromPayload({
            phone_country_dial_code: person?.phoneCountryDialCode,
            phone_area_code: person?.phoneAreaCode,
            phone_local_number: person?.phoneLocalNumber,
            phone_number: person?.phoneNumber,
          }),
        );
        setDadosPhase(DADOS_FORM);
        setFormError(intl.get("baas.users.errors.alreadyMember"));
        addToast({
          type: "info",
          title: intl.get("baas.users.lookup.alreadyLinked"),
        });
        return;
      }

      if (!result.found) {
        setPersonFound(false);
        setHasPlatformAccess(false);
        setAlreadyLinked(null);
        setFieldsLocked(false);
        setName("");
        setEmail("");
        setPhone(createEmptyPhoneParts());
        setDadosPhase(DADOS_FORM);
        setFormError(intl.get("baas.users.lookup.notFound"));
        return;
      }

      const person = result.person;
      setPersonFound(true);
      setHasPlatformAccess(Boolean(result.hasPlatformAccess));
      setAlreadyLinked(null);
      setFieldsLocked(true);
      setNationalRegistration(formatDocument(person?.nationalRegistration || docDigits));
      setName(
        [person?.name, person?.middleName, person?.lastName].filter(Boolean).join(" ").trim()
          || person?.name
          || "",
      );
      setEmail(person?.email ?? "");
      setPhone(
        mapPhonePartsFromPayload({
          phone_country_dial_code: person?.phoneCountryDialCode,
          phone_area_code: person?.phoneAreaCode,
          phone_local_number: person?.phoneLocalNumber,
          phone_number: person?.phoneNumber,
        }),
      );
      if (person?.entityId) {
        setEntityId(String(person.entityId));
        setResolvedEntityIdFallback(String(person.entityId));
      }
      setDadosPhase(DADOS_FORM);
      // Stay on Dados — Continuar advances to Acesso (wizard).
      setCreateStep(CREATE_STEP_DADOS);
      addToast({
        type: "success",
        title: result.hasPlatformAccess
          ? intl.get("baas.users.lookup.platformAccessFound")
          : intl.get("baas.users.lookup.found"),
      });
    } catch (error) {
      setFormError(
        error?.detail || error?.message || intl.get("baas.users.errors.lookupFailed"),
      );
    } finally {
      setLookingUp(false);
    }
  };

  const handleChangeDocument = () => {
    setDadosPhase(DADOS_DOC);
    setPersonFound(false);
    setHasPlatformAccess(false);
    setAlreadyLinked(null);
    setFieldsLocked(false);
    setName("");
    setEmail("");
    setPhone(createEmptyPhoneParts());
    setActiveTab(TAB_DADOS);
    setCreateStep(CREATE_STEP_DADOS);
    setFormError(null);
  };

  const validateIdentityForContinue = () => {
    const docDigits = digitsOnly(nationalRegistration);
    const needsCreateFields = !personFound;
    const phoneReady = isPhonePartsComplete(phone);
    const identityReady =
      (docDigits.length === 11 || docDigits.length === 14) &&
      phoneReady &&
      (!needsCreateFields || (Boolean(name.trim()) && email.trim().includes("@")));

    if (!identityReady) {
      if (!phoneReady) {
        setFormError(intl.get("baas.users.errors.phoneRequired"));
      } else if (needsCreateFields) {
        setFormError(intl.get("baas.users.errors.personDataRequired"));
      } else {
        setFormError(intl.get("baas.users.lookup.searchFirst"));
      }
      return false;
    }
    return true;
  };

  const handleContinueToAccess = () => {
    if (dadosPhase === DADOS_DOC) {
      void handleLookupDocument();
      return;
    }
    if (alreadyLinked) return;
    if (!validateIdentityForContinue()) return;
    setFormError(null);
    setCreateStep(CREATE_STEP_ACCESS);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Create wizard: step 1 Continuar only; final save is step 2.
    if (!isEdit && createStep === CREATE_STEP_DADOS) {
      handleContinueToAccess();
      return;
    }

    const docDigits = digitsOnly(nationalRegistration);
    if (!isMaster && role === "super_user") {
      setFormError(intl.get("baas.users.errors.superUserMasterOnly"));
      return;
    }

    const hasOrgTargets =
      (isMaster && role === "super_user") ||
      existingMemberships.length > 0 ||
      (canManageInstanceLinks &&
        selectedInstanceIds.some((id) => !lockedInstanceIds.has(String(id))));
    const hasTargets = isInstanceScope ? Boolean(fixedInstance) : hasOrgTargets;
    const needsCreateFields = !isEdit && !personFound;
    const phoneReady = isEdit || isPhonePartsComplete(phone);
    const identityReady =
      isEdit ||
      ((docDigits.length === 11 || docDigits.length === 14) &&
        phoneReady &&
        (!needsCreateFields || (Boolean(name.trim()) && email.trim().includes("@"))));

    if (!identityReady || !hasTargets) {
      if (!isEdit) {
        setCreateStep(!identityReady ? CREATE_STEP_DADOS : CREATE_STEP_ACCESS);
      } else {
        setActiveTab(
          !identityReady
            ? TAB_DADOS
            : (isInstanceScope || !canManageInstanceLinks ? TAB_ACCESS : TAB_INSTANCES),
        );
      }
      if (!identityReady && !phoneReady) {
        setFormError(intl.get("baas.users.errors.phoneRequired"));
      } else if (!identityReady && needsCreateFields) {
        setFormError(intl.get("baas.users.errors.personDataRequired"));
      } else if (!hasTargets) {
        setFormError(intl.get("baas.users.errors.instanceRequired"));
      } else if (!identityReady) {
        setFormError(intl.get("baas.users.lookup.searchFirst"));
      }
      return;
    }

    if (isInstanceScope && !canViewProduction && !canViewHomolog) {
      setFormError(intl.get("baas.users.errors.envViewRequired"));
      if (!isEdit) setCreateStep(CREATE_STEP_ACCESS);
      else setActiveTab(TAB_ACCESS);
      return;
    }

    setSubmitting(true);
    setFormError(null);
    try {
      const phoneE164 = composeE164FromParts(phone).replace(/\D/g, "");
      const identityPayload = {
        nationalRegistration: docDigits || undefined,
        email: email.trim() || undefined,
        name: name.trim() || undefined,
        phone: phoneE164 || undefined,
        phoneCountryDialCode: digitsOnly(phone?.countryDialCode) || undefined,
        phoneAreaCode: digitsOnly(phone?.areaCode) || undefined,
        phoneLocalNumber: digitsOnly(phone?.number) || undefined,
      };

      if (isInstanceScope) {
        await syncBaasUserInstanceAccess({
          ...identityPayload,
          role,
          permissions,
          status,
          canViewProduction,
          canViewHomolog,
          selectedInstances,
          allInstances: instances,
          existingMemberships,
          removeUnselected: false,
        });
      } else if (!isEdit) {
        if (!canManageInstanceLinks) {
          setFormError(intl.get("baas.users.membership.instanceLinksLocked"));
          setSubmitting(false);
          return;
        }
        await syncBaasUserInstanceAccess({
          ...identityPayload,
          role,
          permissions,
          status: "active",
          selectedInstances:
            role === "super_user"
              ? []
              : pickerInstances.filter((item) =>
                  selectedInstanceIds.includes(String(item.id)),
                ),
          allInstances: instances,
          existingMemberships: [],
          removeUnselected: false,
        });
      } else if (role === "super_user" && existingMemberships.length === 0) {
        if (!isMaster) {
          setFormError(intl.get("baas.users.errors.superUserMasterOnly"));
          setSubmitting(false);
          return;
        }
        await syncBaasUserInstanceAccess({
          ...identityPayload,
          role,
          permissions,
          status: "active",
          selectedInstances: [],
          allInstances: instances,
          existingMemberships: [],
          removeUnselected: false,
        });
      } else {
        // Org edit: master may add new links; scoped admin only updates role/perms on existing.
        const newInstances = canManageInstanceLinks
          ? pickerInstances.filter(
              (item) =>
                selectedInstanceIds.includes(String(item.id)) &&
                !lockedInstanceIds.has(String(item.id)),
            )
          : [];
        for (const instance of newInstances) {
          await createBaasUser(instance, {
            ...identityPayload,
            role,
            permissions,
            status: "active",
          });
        }
        let updatedCount = 0;
        let skippedUnresolved = 0;
        for (const membership of existingMemberships) {
          if (!membership?.id) {
            skippedUnresolved += 1;
            continue;
          }
          const instance = resolveInstanceForMembership(membership);
          if (!instance) {
            skippedUnresolved += 1;
            continue;
          }
          await updateBaasUser(instance, membership.id, {
            role,
            permissions,
            status: membership.status,
            groupId: membership.groupId ?? null,
          });
          updatedCount += 1;
        }
        if (
          existingMemberships.length > 0 &&
          newInstances.length === 0 &&
          updatedCount === 0
        ) {
          const err = new Error(
            skippedUnresolved > 0
              ? intl.get("baas.users.membership.instanceResolveFailed")
              : intl.get("baas.users.saveError"),
          );
          err.detail = err.message;
          throw err;
        }
      }

      if (
        isInstanceScope &&
        entityFeaturesDirty &&
        entityContextInstance &&
        lockedEntityId
      ) {
        const toSave = BAAS_ENTITY_FEATURE_OPTIONS
          .filter((option) => entityFeatureSource[option.code] === "entity")
          .map((option) => ({
            feature_code: option.code,
            enabled: Boolean(entityFeatureEnabled[option.code]),
          }));

        if (toSave.length > 0) {
          await setBaasEntityFeatures(entityContextInstance, lockedEntityId, toSave);
        }
      }

      addToast({
        type: "success",
        title: intl.get(
          isEdit
            ? "baas.users.editSuccess"
            : hasPlatformAccess
              ? "baas.users.createSuccessGrant"
              : "baas.users.createSuccess",
        ),
      });

      onSaved?.();
      handleClose();
    } catch (error) {
      setFormError(error?.detail || error?.message || intl.get("baas.users.saveError"));
      setSubmitting(false);
    }
  };

  const tabs = isInstanceScope
    ? [
        { id: TAB_DADOS, label: intl.get("baas.users.tabs.dados") },
        { id: TAB_ACCESS, label: intl.get("baas.users.tabs.access") },
      ]
    : canManageInstanceLinks
      ? [
          { id: TAB_DADOS, label: intl.get("baas.users.tabs.dados") },
          { id: TAB_INSTANCES, label: intl.get("baas.users.tabs.instances") },
        ]
      : [
          { id: TAB_DADOS, label: intl.get("baas.users.tabs.dados") },
          { id: TAB_ACCESS, label: intl.get("baas.users.tabs.access") },
        ];

  const createWizardSteps = [
    { id: "dados", label: intl.get("baas.users.steps.dados") },
    { id: "access", label: intl.get("baas.users.steps.access") },
  ];

  const showDadosPanel = isEdit
    ? activeTab === TAB_DADOS
    : createStep === CREATE_STEP_DADOS;
  const showInstancesPanel =
    !isInstanceScope &&
    canManageInstanceLinks &&
    (isEdit
      ? activeTab === TAB_INSTANCES
      : createStep === CREATE_STEP_ACCESS);
  const showAccessPanel =
    (isInstanceScope || !canManageInstanceLinks) &&
    (isEdit ? activeTab === TAB_ACCESS : createStep === CREATE_STEP_ACCESS);

  const subtitleKey = isInstanceScope
    ? isEdit
      ? "baas.users.editSubtitle"
      : "baas.users.createSubtitle"
    : isEdit
      ? "baas.users.editSubtitleOrg"
      : "baas.users.createSubtitleOrg";

  const createSubmitDisabled =
    submitting ||
    lookingUp ||
    Boolean(membershipActionId) ||
    roleSelectLocked ||
    Boolean(alreadyLinked) ||
    (isInstanceScope
      ? !fixedInstance
      : canManageInstanceLinks
        ? role !== "super_user" &&
          existingMemberships.length === 0 &&
          !selectedInstanceIds.some((id) => !lockedInstanceIds.has(String(id)))
        : existingMemberships.length === 0);
  const renderEntityProducts = () => (
    <div className="border-t border-gray-200 pt-4 dark:border-brand-dark-border">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-brand-dark dark:text-gray-100">
          {intl.get("baas.users.entityPermissions.sectionTitle")}
        </h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.users.entityPermissions.hintInstance", {
            instance: fixedInstance?.name ?? "",
          })}
        </p>
      </div>

      <div>
        {lockedEntityId ? (
          <>
            <BaasSearchableSelect
              id="entity-profile"
              label={intl.get("baas.users.entityPermissions.entity")}
              value={lockedEntityId}
              onChange={() => {}}
              options={lockedEntitySelectOptions}
              getOptionLabel={(entity) => entity.label || entity.name}
              getOptionSecondary={(entity) =>
                [entity.type, entity.id].filter(Boolean).join(" · ")
              }
              getOptionSearchText={(entity) =>
                [entity.name, entity.label, entity.id, entity.type]
                  .filter(Boolean)
                  .join(" ")
              }
              placeholder={
                entityOptionsLoading
                  ? intl.get("baas.users.entityPermissions.loadingEntities")
                  : lockedEntityId
              }
              locked
              disabled
            />
            <p className="mt-1 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
              <TbLock className="shrink-0" size={12} aria-hidden />
              {intl.get("baas.users.entityPermissions.entityLockedHint")}
            </p>
          </>
        ) : (
          <>
            <p className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-brand-dark dark:text-gray-200">
              <TbLock className="shrink-0 text-gray-400" size={14} aria-hidden />
              {intl.get("baas.users.entityPermissions.entity")}
            </p>
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
              {intl.get("baas.users.entityPermissions.entityLockedEmpty")}
            </p>
          </>
        )}
      </div>

      {lockedEntityId ? (
        <>
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.users.entityPermissions.editHint")}
          </p>

          {entityFeaturesError ? (
            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
              {entityFeaturesError}
            </p>
          ) : null}

          {entityFeaturesLoading ? (
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              {intl.get("baas.users.entityPermissions.loadingFeatures")}
            </p>
          ) : (
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {(entityFeatureSource.cards ?? "instance") !== "denied_by_instance" ? (
                <p className="sm:col-span-2 rounded-lg border border-[#e7a73f]/40 bg-[#e7a73f]/10 px-3 py-2 text-xs text-brand-dark dark:text-gray-100">
                  {intl.get("baas.users.entityPermissions.cardsIncludes")}
                </p>
              ) : null}
              {BAAS_ENTITY_FEATURE_OPTIONS.filter((option) => {
                // Hide products the instance does not permit — no "bloqueado" ghosts.
                const source = entityFeatureSource[option.code] ?? "instance";
                return source !== "denied_by_instance";
              }).map((option) => {
                const source = entityFeatureSource[option.code] ?? "instance";
                const denied = source === "denied_by_instance";
                const switchDisabled = submitting || (denied && !isValutAdmin);
                return (
                  <FeatureToggleRow
                    key={option.code}
                    label={intl.get(featureLabelKey(option.code))}
                    hint={intl.get(featureHintKey(option.code))}
                    source={source}
                    checked={Boolean(entityFeatureEnabled[option.code])}
                    disabled={switchDisabled}
                    onChange={(enabled) => toggleEntityFeature(option.code, enabled)}
                    footer={
                      denied ? (
                        <p className="mt-2 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
                          {intl.get("baas.users.entityPermissions.deniedHint")}{" "}
                          <Link
                            to="/baas/configuracoes"
                            className="font-medium text-[#e7a73f] underline-offset-2 hover:underline"
                            onClick={handleClose}
                          >
                            {intl.get("baas.users.entityPermissions.openSettings")}
                          </Link>
                          {isValutAdmin
                            ? ` ${intl.get("baas.users.entityPermissions.adminDeniedHint")}`
                            : null}
                        </p>
                      ) : source === "instance" ? (
                        <p className="mt-2 text-[11px] text-gray-400">
                          {intl.get("baas.users.entityPermissions.inheritedHint")}
                        </p>
                      ) : null
                    }
                  />
                );
              })}
            </div>
          )}
        </>
      ) : null}
    </div>
  );

  const renderRoleAndPermissions = () => (
    <>
      <div>
        <label
          htmlFor="user-role"
          className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-200"
        >
          {intl.get("baas.users.columns.role")}
        </label>
        <select
          id="user-role"
          value={role}
          onChange={(event) => handleRoleChange(event.target.value)}
          className={roleSelectLocked ? disabledInputClass : inputClass}
          disabled={roleSelectLocked || submitting}
        >
          {roleOptions.map((roleOption) => (
            <option key={roleOption} value={roleOption}>
              {intl.get(roleLabelKey[roleOption])}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {intl.get(
            role === "super_user"
              ? "baas.users.roleSuperUserHint"
              : !isMaster
                ? "baas.users.roleScopedAdminHint"
                : isInstanceScope
                  ? "baas.users.roleInstanceHint"
                  : "baas.users.roleInheritedHint",
          )}
        </p>
      </div>

      {role === "super_user" ? null : (
      <div>
        <p className="mb-2 text-sm font-medium text-brand-dark dark:text-gray-200">
          {intl.get("baas.users.permissionsLabel")}
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {BAAS_GROUP_PERMISSIONS.map((permission) => (
            <label
              key={permission}
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 transition hover:border-[#e7a73f]/40 dark:border-brand-dark-border dark:text-gray-300"
            >
              <input
                type="checkbox"
                checked={permissions.includes(permission)}
                onChange={() => togglePermission(permission)}
                className={checkboxClass}
              />
              <span className="font-mono text-xs">{permission}</span>
            </label>
          ))}
        </div>
      </div>
      )}

      {isInstanceScope ? (
        <div>
          <p className="mb-2 text-sm font-medium text-brand-dark dark:text-gray-200">
            {intl.get("baas.users.envAccessLabel")}
          </p>
          <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.users.envAccessHint")}
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 transition hover:border-[#e7a73f]/40 dark:border-brand-dark-border dark:text-gray-300">
              <input
                type="checkbox"
                checked={role === "super_user" ? true : canViewProduction}
                onChange={() => setCanViewProduction((current) => !current)}
                className={checkboxClass}
                disabled={submitting || role === "super_user"}
              />
              <span>{intl.get("baas.users.canViewProduction")}</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 transition hover:border-[#e7a73f]/40 dark:border-brand-dark-border dark:text-gray-300">
              <input
                type="checkbox"
                checked={role === "super_user" ? true : canViewHomolog}
                onChange={() => setCanViewHomolog((current) => !current)}
                className={checkboxClass}
                disabled={submitting || role === "super_user"}
              />
              <span>{intl.get("baas.users.canViewHomolog")}</span>
            </label>
          </div>
        </div>
      ) : null}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ className: MODAL_CLASS }}
      BackdropProps={{
        sx: { backgroundColor: "rgba(36, 36, 36, 0.45)" },
      }}
    >
      <form onSubmit={handleSubmit} className="relative p-6 outline-none">
        <ModalCloseButton
          onClick={handleClose}
          className="absolute right-3 top-3"
          disabled={submitting || Boolean(membershipActionId)}
        />
        <div className="mb-5 flex items-center gap-3 pr-8">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#e7a73f]/10 text-[#e7a73f]">
            {isEdit ? <TbPencil size={20} /> : <TbUserPlus size={20} />}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-brand-dark dark:text-gray-100">
              {intl.get(isEdit ? "baas.users.editTitle" : "baas.users.createTitle")}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {intl.get(subtitleKey, {
                instance: fixedInstance?.name ?? "",
              })}
            </p>
          </div>
        </div>

        {!isEdit ? (
          <CreateUserStepper currentStep={createStep} steps={createWizardSteps} />
        ) : (
          <div
            className="extract-view-tabs mb-5"
            role="tablist"
            aria-label={intl.get("baas.users.tabsLabel")}
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`extract-view-tab focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#e7a73f]/50 ${
                  activeTab === tab.id ? "extract-view-tab--active" : ""
                }`}
                onClick={() => {
                  setFormError(null);
                  setActiveTab(tab.id);
                }}
                disabled={submitting || lookingUp}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {formError ? (
          <p
            role="alert"
            className="mb-4 rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
          >
            {formError}
          </p>
        ) : null}

        <div className="space-y-4">
          {showDadosPanel ? (
            <>
              {!isEdit && dadosPhase === DADOS_DOC ? (
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="user-document"
                      className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-200"
                    >
                      {intl.get("baas.users.columns.document")}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <input
                        id="user-document"
                        type="text"
                        inputMode="numeric"
                        value={nationalRegistration}
                        onChange={(event) =>
                          setNationalRegistration(formatDocument(event.target.value))
                        }
                        placeholder={intl.get("baas.users.documentPlaceholder")}
                        className={`${inputClass} min-w-[220px] flex-1`}
                        disabled={lookingUp || submitting}
                        autoFocus
                        required
                      />
                      <button
                        type="button"
                        className="btn-primary inline-flex h-10 items-center gap-1.5 px-4"
                        disabled={lookingUp || submitting}
                        onClick={handleLookupDocument}
                      >
                        <TbSearch size={16} aria-hidden />
                        {lookingUp
                          ? intl.get("baas.users.actions.searching")
                          : intl.get("baas.users.actions.search")}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {intl.get("baas.users.documentHint")}
                    </p>
                  </div>
                  <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                    {intl.get("baas.users.lookup.hint")}
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <label
                      htmlFor="user-document"
                      className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-200"
                    >
                      {intl.get("baas.users.columns.document")}
                    </label>
                    <div className="flex flex-wrap items-end gap-2">
                      <input
                        id="user-document"
                        type="text"
                        inputMode="numeric"
                        value={nationalRegistration}
                        onChange={(event) =>
                          setNationalRegistration(formatDocument(event.target.value))
                        }
                        placeholder={intl.get("baas.users.documentPlaceholder")}
                        className={
                          isEdit || fieldsLocked
                            ? `${disabledInputClass} min-w-[220px] flex-1`
                            : `${inputClass} min-w-[220px] flex-1`
                        }
                        disabled={isEdit || fieldsLocked || submitting}
                        required={!isEdit}
                      />
                      {!isEdit && fieldsLocked ? (
                        <button
                          type="button"
                          className="btn-outline h-10"
                          onClick={handleChangeDocument}
                          disabled={submitting}
                        >
                          {intl.get("baas.users.actions.changeDocument")}
                        </button>
                      ) : null}
                    </div>
                    {!isEdit ? (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {alreadyLinked
                          ? intl.get("baas.users.lookup.alreadyLinkedHint")
                          : personFound
                            ? hasPlatformAccess
                              ? intl.get("baas.users.lookup.platformAccessBanner")
                              : intl.get("baas.users.lookup.foundBanner")
                            : intl.get("baas.users.lookup.createHint")}
                      </p>
                    ) : null}
                    {!isEdit && alreadyLinked && String(email || "").includes("@") ? (
                      <button
                        type="button"
                        className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-white disabled:opacity-50 dark:border-brand-dark-border dark:text-gray-300 dark:hover:bg-brand-dark-elevated"
                        disabled={resendingAccess || submitting || !alreadyLinked.userId}
                        title={intl.get("baas.users.access.resendHint")}
                        onClick={() => {
                          void handleResendAccessEmail();
                        }}
                      >
                        <TbMailForward size={14} aria-hidden />
                        {intl.get("baas.users.access.resend")}
                      </button>
                    ) : null}
                  </div>

                  <div>
                    <label
                      htmlFor="user-name"
                      className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-200"
                    >
                      {intl.get("baas.users.columns.name")}
                    </label>
                    <input
                      id="user-name"
                      type="text"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      placeholder={intl.get("baas.users.namePlaceholder")}
                      className={
                        isEdit || (fieldsLocked && name) ? disabledInputClass : inputClass
                      }
                      disabled={isEdit || (fieldsLocked && Boolean(name)) || submitting}
                      required={!isEdit && !personFound}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="user-email"
                      className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-200"
                    >
                      {intl.get("baas.users.columns.email")}
                    </label>
                    <input
                      id="user-email"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder={intl.get("baas.users.emailPlaceholder")}
                      className={
                        isEdit || (fieldsLocked && email) ? disabledInputClass : inputClass
                      }
                      disabled={isEdit || (fieldsLocked && Boolean(email)) || submitting}
                      required={!isEdit && !personFound}
                    />
                  </div>

                  {!isEdit ? (
                    <div>
                      <p className="mb-1.5 text-sm font-medium text-brand-dark dark:text-gray-200">
                        {intl.get("baas.users.columns.phone")}
                      </p>
                      <PhonePartsFields
                        value={phone}
                        onChange={(next) => setPhone((current) => ({ ...current, ...next }))}
                        dialOptions={phoneDialOptions}
                        required
                        disabled={submitting}
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {intl.get("baas.users.phoneHint")}
                      </p>
                    </div>
                  ) : null}
                </>
              )}

              {isEdit ? (
                <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-3 dark:border-brand-dark-border dark:bg-brand-dark-elevated/30">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-brand-dark dark:text-gray-200">
                        {intl.get("baas.users.columns.access")}
                      </p>
                      <span
                        className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          accessStatusStyles[editingPerson?.accessStatus || "pending"] ??
                          accessStatusStyles.pending
                        }`}
                        title={intl.get(
                          `baas.users.access.hint.${editingPerson?.accessStatus || "pending"}`,
                        )}
                      >
                        {intl.get(
                          `baas.users.access.status.${editingPerson?.accessStatus || "pending"}`,
                        )}
                      </span>
                    </div>
                    {String(email || "").includes("@") &&
                    editingPerson?.accessStatus !== "no_email" ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-white disabled:opacity-50 dark:border-brand-dark-border dark:text-gray-300 dark:hover:bg-brand-dark-elevated"
                        disabled={resendingAccess || submitting}
                        title={intl.get("baas.users.access.resendHint")}
                        onClick={() => {
                          void handleResendAccessEmail();
                        }}
                      >
                        <TbMailForward size={14} aria-hidden />
                        {intl.get("baas.users.access.resend")}
                      </button>
                    ) : null}
                  </div>

                  <div className="border-t border-gray-200 pt-3 dark:border-brand-dark-border">
                    <p className="mb-1 text-sm font-medium text-brand-dark dark:text-gray-200">
                      {intl.get("baas.users.access.passwordTitle")}
                    </p>
                    <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                      {intl.get("baas.users.access.passwordHint")}
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div>
                        <label
                          htmlFor="user-login-password"
                          className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400"
                        >
                          {intl.get("baas.users.access.passwordLabel")}
                        </label>
                        <input
                          id="user-login-password"
                          type="password"
                          autoComplete="new-password"
                          value={loginPassword}
                          onChange={(event) => setLoginPassword(event.target.value)}
                          placeholder={intl.get("baas.users.access.passwordPlaceholder")}
                          className={inputClass}
                          disabled={savingLoginPassword || submitting}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="user-login-password-confirm"
                          className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400"
                        >
                          {intl.get("baas.users.access.passwordConfirmLabel")}
                        </label>
                        <input
                          id="user-login-password-confirm"
                          type="password"
                          autoComplete="new-password"
                          value={loginPasswordConfirm}
                          onChange={(event) => setLoginPasswordConfirm(event.target.value)}
                          placeholder={intl.get("baas.users.access.passwordConfirmPlaceholder")}
                          className={inputClass}
                          disabled={savingLoginPassword || submitting}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-white disabled:opacity-50 dark:border-brand-dark-border dark:text-gray-300 dark:hover:bg-brand-dark-elevated"
                      disabled={savingLoginPassword || submitting || !loginPassword}
                      onClick={() => {
                        void handleSetLoginPassword();
                      }}
                    >
                      <TbLock size={14} aria-hidden />
                      {savingLoginPassword
                        ? intl.get("baas.users.access.passwordSaving")
                        : intl.get("baas.users.access.passwordSave")}
                    </button>
                  </div>

                  <div className="border-t border-gray-200 pt-3 dark:border-brand-dark-border">
                    <p className="mb-1 text-sm font-medium text-brand-dark dark:text-gray-200">
                      {intl.get("baas.users.access.txPasswordTitle")}
                    </p>
                    <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                      {intl.get("baas.users.access.txPasswordHint")}
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div>
                        <label
                          htmlFor="user-tx-password"
                          className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400"
                        >
                          {intl.get("baas.users.access.txPasswordLabel")}
                        </label>
                        <input
                          id="user-tx-password"
                          type="password"
                          inputMode="numeric"
                          autoComplete="new-password"
                          maxLength={4}
                          value={txPassword}
                          onChange={(event) =>
                            setTxPassword(digitsOnly(event.target.value).slice(0, 4))
                          }
                          placeholder={intl.get("baas.users.access.txPasswordPlaceholder")}
                          className={inputClass}
                          disabled={savingTxPassword || submitting}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="user-tx-password-confirm"
                          className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400"
                        >
                          {intl.get("baas.users.access.txPasswordConfirmLabel")}
                        </label>
                        <input
                          id="user-tx-password-confirm"
                          type="password"
                          inputMode="numeric"
                          autoComplete="new-password"
                          maxLength={4}
                          value={txPasswordConfirm}
                          onChange={(event) =>
                            setTxPasswordConfirm(digitsOnly(event.target.value).slice(0, 4))
                          }
                          placeholder={intl.get("baas.users.access.txPasswordConfirmPlaceholder")}
                          className={inputClass}
                          disabled={savingTxPassword || submitting}
                        />
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-white disabled:opacity-50 dark:border-brand-dark-border dark:text-gray-300 dark:hover:bg-brand-dark-elevated"
                        disabled={savingTxPassword || resettingTxPassword || submitting || !txPassword}
                        onClick={() => {
                          void handleSetTransactionalPassword();
                        }}
                      >
                        <TbKey size={14} aria-hidden />
                        {savingTxPassword
                          ? intl.get("baas.users.access.txPasswordSaving")
                          : intl.get("baas.users.access.txPasswordSave")}
                      </button>
                      {isInstanceScope ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 px-3 py-1.5 text-xs font-medium text-amber-800 transition hover:bg-amber-50 disabled:opacity-50 dark:border-amber-900/40 dark:text-amber-200 dark:hover:bg-amber-950/30"
                          disabled={savingTxPassword || resettingTxPassword || submitting}
                          onClick={() => {
                            void handleResetTransactionalPassword();
                          }}
                        >
                          <TbLockOff size={14} aria-hidden />
                          {resettingTxPassword
                            ? intl.get("baas.users.access.txPasswordResetting")
                            : intl.get("baas.users.access.txPasswordReset")}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}

              {isEdit && isInstanceScope ? (
                <div>
                  <label
                    htmlFor="user-status"
                    className="mb-1.5 block text-sm font-medium text-brand-dark dark:text-gray-200"
                  >
                    {intl.get("baas.users.columns.status")}
                  </label>
                  <select
                    id="user-status"
                    value={status}
                    onChange={(event) => setStatus(event.target.value)}
                    className={inputClass}
                  >
                    <option value="active">{intl.get("baas.users.status.active")}</option>
                    <option value="suspended">{intl.get("baas.users.status.suspended")}</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {intl.get("baas.users.membership.statusHint")}
                  </p>
                </div>
              ) : null}

              {isEdit && personEntityId ? (
                <div>
                  <p className="mb-1.5 text-sm font-medium text-brand-dark dark:text-gray-200">
                    {intl.get("baas.users.entityPermissions.personEntity")}
                  </p>
                  <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-600 dark:border-brand-dark-border dark:bg-brand-dark-elevated/40 dark:text-gray-300">
                    {personEntityId}
                  </p>
                </div>
              ) : null}

              {isEdit && isInstanceScope ? (
                <div className="flex flex-wrap gap-2 border-t border-gray-200 pt-4 dark:border-brand-dark-border">
                  {existingMemberships
                    .filter((m) =>
                      fixedInstance
                        ? String(m.instanceId) === String(fixedInstance.id)
                        : true,
                    )
                    .map((membership) => (
                      <div key={membership.id} className="flex flex-wrap gap-2">
                        {membership.status !== "suspended" ? (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-brand-dark-border dark:text-gray-300 dark:hover:bg-brand-dark-elevated/40"
                            disabled={Boolean(membershipActionId) || submitting}
                            onClick={() => handleMembershipStatus(membership, "suspended")}
                          >
                            <TbUserOff size={14} aria-hidden />
                            {intl.get("baas.users.membership.deactivate")}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 px-3 py-1.5 text-xs font-medium text-emerald-700 transition hover:bg-emerald-50 dark:border-emerald-900/40 dark:text-emerald-300 dark:hover:bg-emerald-950/30"
                            disabled={Boolean(membershipActionId) || submitting}
                            onClick={() => handleMembershipStatus(membership, "active")}
                          >
                            {intl.get("baas.users.membership.activate")}
                          </button>
                        )}
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 transition hover:bg-red-50 dark:border-red-900/40 dark:text-red-300 dark:hover:bg-red-950/30"
                          disabled={Boolean(membershipActionId) || submitting}
                          onClick={() => handleRemoveMembership(membership)}
                        >
                          <TbTrash size={14} aria-hidden />
                          {intl.get("baas.users.membership.remove")}
                        </button>
                      </div>
                    ))}
                </div>
              ) : null}

              {!isEdit && dadosPhase === DADOS_FORM && !alreadyLinked ? (
                <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                  {intl.get("baas.users.steps.dadosNextHint")}
                </p>
              ) : null}
            </>
          ) : null}

          {showInstancesPanel ? (
            <>
              {role === "super_user" ? (
                <div className="mb-4 rounded-xl border border-[#e7a73f]/30 bg-[#fff8ec] px-4 py-3 dark:border-[#e7a73f]/25 dark:bg-brand-dark-surface">
                  <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                    {intl.get("baas.users.instancesAllSummary")}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-gray-600 dark:text-gray-400">
                    {intl.get("baas.users.instancesAllSummaryHint")}
                  </p>
                </div>
              ) : null}
              <div>
                <p className="mb-2 text-sm font-medium text-brand-dark dark:text-gray-200">
                  {role === "super_user"
                    ? intl.get("baas.users.superUserLinksAuditLabel")
                    : intl.get("baas.users.instancesLabel")}
                </p>
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  {role === "super_user"
                    ? intl.get("baas.users.superUserInstancesOptional")
                    : intl.get("baas.users.membership.lockedHint")}
                </p>

                {existingMemberships.length > 0 ? (
                  <div className="mb-3 space-y-2">
                    {existingMemberships.map((membership) => {
                      const instance = resolveInstanceForMembership(membership);
                      const name =
                        instance?.name ??
                        membership.baasInstanceId ??
                        membership.instanceId;
                      const busy = membershipActionId === membership.id;
                      return (
                        <div
                          key={membership.id}
                          className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between dark:border-brand-dark-border dark:bg-brand-dark-elevated/30"
                        >
                          <div className="flex min-w-0 items-center gap-2">
                            <TbLock
                              className="shrink-0 text-gray-400"
                              size={16}
                              aria-hidden
                            />
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-brand-dark dark:text-gray-100">
                                {name}
                              </p>
                              <span
                                className={`mt-0.5 inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                  statusStyles[membership.status] ?? statusStyles.suspended
                                }`}
                              >
                                {intl.get(`baas.users.status.${membership.status}`)}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {membership.status !== "suspended" ? (
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 transition hover:bg-white dark:border-brand-dark-border dark:text-gray-300 dark:hover:bg-brand-dark-elevated"
                                disabled={busy || submitting}
                                onClick={() =>
                                  handleMembershipStatus(membership, "suspended")
                                }
                              >
                                <TbUserOff size={14} aria-hidden />
                                {intl.get("baas.users.membership.deactivate")}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 px-2.5 py-1 text-xs font-medium text-emerald-700 transition hover:bg-emerald-50 dark:border-emerald-900/40 dark:text-emerald-300"
                                disabled={busy || submitting}
                                onClick={() =>
                                  handleMembershipStatus(membership, "active")
                                }
                              >
                                {intl.get("baas.users.membership.activate")}
                              </button>
                            )}
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1 text-xs font-medium text-red-700 transition hover:bg-red-50 dark:border-red-900/40 dark:text-red-300"
                              disabled={busy || submitting}
                              onClick={() => handleRemoveMembership(membership)}
                            >
                              <TbTrash size={14} aria-hidden />
                              {intl.get("baas.users.membership.remove")}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-brand-dark-border">
                  {addablePickerInstances.length > 5 ? (
                    <div className="border-b border-gray-100 p-2 dark:border-brand-dark-border">
                      <div className="relative">
                        <TbSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                          type="search"
                          value={instanceFilter}
                          onChange={(event) => setInstanceFilter(event.target.value)}
                          placeholder={intl.get("baas.users.instancesSearchPlaceholder")}
                          className="h-9 w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] focus:ring-2 focus:ring-[#e7a73f]/20 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100"
                          aria-label={intl.get("baas.users.instancesSearchPlaceholder")}
                        />
                      </div>
                    </div>
                  ) : null}
                  <div className="max-h-56 space-y-1 overflow-y-auto p-2">
                    {pickerInstances.length === 0 ? (
                      <p className="px-2 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {intl.get("baas.users.noInstances")}
                      </p>
                    ) : addablePickerInstances.length === 0 ? (
                      <p className="px-2 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {intl.get("baas.users.membership.noMoreInstances")}
                      </p>
                    ) : filteredAddableInstances.length === 0 ? (
                      <p className="px-2 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {intl.get("baas.users.instancesSearchEmpty")}
                      </p>
                    ) : (
                      filteredAddableInstances.map((instance) => {
                        const checked = selectedInstanceIds.includes(String(instance.id));
                        return (
                          <label
                            key={instance.id}
                            className={`flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition ${
                              checked
                                ? "bg-[#fff8ec] text-brand-dark dark:bg-brand-dark-surface dark:text-gray-100"
                                : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-brand-dark-elevated/40"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleAddableInstance(instance.id)}
                              className={checkboxClass}
                            />
                            <span className="min-w-0 flex-1 truncate font-medium">
                              {instance.name}
                            </span>
                            {instance.environment ? (
                              <span className="shrink-0 text-xs uppercase text-gray-400">
                                {instance.environment}
                              </span>
                            ) : null}
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {intl.get("baas.users.instancesHintAdd")}
                </p>
              </div>

              {renderRoleAndPermissions()}
            </>
          ) : null}

          {showAccessPanel ? (
            <>
              {renderRoleAndPermissions()}
              {isInstanceScope ? renderEntityProducts() : null}
            </>
          ) : null}
        </div>

        <div
          className={`mt-6 flex gap-2 ${
            !isEdit && createStep === CREATE_STEP_ACCESS ? "justify-between" : "justify-end"
          }`}
        >
          {!isEdit && createStep === CREATE_STEP_ACCESS ? (
            <button
              type="button"
              className="btn-back"
              disabled={submitting}
              onClick={() => {
                setFormError(null);
                setCreateStep(CREATE_STEP_DADOS);
              }}
            >
              <TbArrowLeft className="h-4 w-4" aria-hidden />
              {intl.get("baas.users.actions.back")}
            </button>
          ) : null}

          {!isEdit && createStep === CREATE_STEP_DADOS ? (
            <button
              type="submit"
              className="btn-primary"
              disabled={
                submitting ||
                lookingUp ||
                Boolean(alreadyLinked) ||
                dadosPhase === DADOS_DOC
              }
            >
              {intl.get("baas.users.actions.continue")}
            </button>
          ) : (
            <button
              type="submit"
              className="btn-primary"
              disabled={
                isEdit
                  ? submitting ||
                    lookingUp ||
                    Boolean(membershipActionId) ||
                    roleSelectLocked ||
                    (isInstanceScope
                      ? !fixedInstance
                      : canManageInstanceLinks
                        ? role !== "super_user" &&
                          existingMemberships.length === 0 &&
                          !selectedInstanceIds.some(
                            (id) => !lockedInstanceIds.has(String(id)),
                          )
                        : existingMemberships.length === 0)
                  : createSubmitDisabled
              }
            >
              {intl.get(isEdit ? "baas.users.editUser" : "baas.users.addUser")}
            </button>
          )}
        </div>
      </form>
    </Dialog>
  );
};
